import { useMemo, useState } from 'react';
import {
  keepPreviousData,
  useMutation,
  useQueryClient,
} from '@tanstack/react-query';
import { useQueryWithErrorHandler } from './useQueryWithErrorHandler';
import {
  IResponseData,
  IError,
  IApiParams,
  IPlantGroup,
  INotificationEntityGroup,
  IAddToNotificationEntity,
  IUpdateNotificationEntityGroup,
  ICreateNotificationEntityGroupRequest,
  IGetPlantCrossShippingGroupPayload,
} from 'types';
import { useAuth, useError } from '../providers';
import { useAuthClient } from './useAuthClient';
import { firebaseAnalytics } from '../analytics';
import { errors } from '../config';

const plantCrossShippingGroupsKey = 'getPlantCrossShippingGroups';
const plantCrossShippingGroupKey = 'getPlantCrossShippingGroup';

export function usePlantCrossShippingGroupApi({
  queryParameters,
  organizationId,
}: IApiParams) {
  const authClient = useAuthClient();
  const { openModal } = useError();
  const { supplierId } = useAuth();
  const queryClient = useQueryClient();

  const currentSupplierId = useMemo(() => {
    return organizationId || supplierId;
  }, [organizationId, supplierId]);

  const getPlantCrossShippingGroupsUrl = useMemo(() => {
    return `/api/v1/${currentSupplierId}/Plant/get-plant-cross-shipping-groups`;
  }, [currentSupplierId]);

  const getPlantCrossShippingGroupUrl = useMemo(() => {
    return `/api/v1/${currentSupplierId}/Plant/get-plant-cross-shipping-group`;
  }, [currentSupplierId]);

  const createPlantCrossShippingGroupUrl = useMemo(() => {
    return `/api/v1/${currentSupplierId}/Plant/create-plant-cross-shipping-group`;
  }, [currentSupplierId]);

  const removePlantFromCrossShippingGroupUrl = useMemo(() => {
    return `/api/v1/${currentSupplierId}/Plant/remove-plant-from-cross-shipping-group`;
  }, [currentSupplierId]);

  const addToPlantCrossShippingGroupUrl = useMemo(() => {
    return `/api/v1/${currentSupplierId}/Plant/add-plant-to-cross-shipping-group`;
  }, [currentSupplierId]);

  const updatePlantCrossShippingGroupUrl = useMemo(() => {
    return `/api/v1/${currentSupplierId}/Plant/update-plant-cross-shipping-group`;
  }, [currentSupplierId]);

  const deletePlantCrossShippingGroupUrl = useMemo(() => {
    return `/api/v1/${currentSupplierId}/Plant/delete-plant-cross-shipping-group`;
  }, [currentSupplierId]);
  const [hasMoreCrossShippingGroups, setHasMoreCrossShippingGroups] =
    useState(true);
  const [crossShippingGroups, setCrossShippingGroups] = useState<
    INotificationEntityGroup[]
  >([]);

  const {
    data: plantCrossShippingGroupsResponse,
    refetch: fetchPlantCrossShippingGroups,
    isFetching: isFetchingPlantCrossShippingGroups,
  } = useQueryWithErrorHandler<
    IResponseData<INotificationEntityGroup[]>,
    IError
  >({
    queryKey: [plantCrossShippingGroupsKey, { queryParameters }],

    queryFn: async () => {
      return await authClient(`${getPlantCrossShippingGroupsUrl}`, {
        queryParameters,
        method: 'GET',
      });
    },

    enabled: false,
    refetchOnWindowFocus: false,

    onError: error => {
      firebaseAnalytics.logErrorEvent(
        errors.getNotificationEntitiesGroups,
        error,
        {
          getPlantCrossShippingGroupsUrl,
          ...queryParameters,
        },
      );
      openModal(error);
    },
  });

  const {
    mutateAsync: fetchCrossShippingGroups,
    isPending: isFetchingCrossShippingGroups,
    error: crossShippingGroupsError,
  } = useMutation({
    mutationFn: async (payload: IGetPlantCrossShippingGroupPayload) => {
      return authClient(getPlantCrossShippingGroupsUrl, {
        queryParameters: {
          ...payload,
          skip: payload.page * payload.take,
        },
        method: 'GET',
      }).then(res => {
        setCrossShippingGroups(prevState => {
          if (!payload.page || (res.data.length && !prevState)) {
            return res.data;
          } else {
            return prevState.concat(res.data);
          }
        });

        if (res.data.length < payload.take) {
          setHasMoreCrossShippingGroups(false);
        }
      });
    },

    retry: false,

    onError: (error: IError) => {
      openModal(error);
    },
  });

  const {
    data: plantCrossShippingGroupResponse,
    refetch: fetchPlantCrossShippingGroup,
    isFetching: isFetchingPlantCrossShippingGroup,
  } = useQueryWithErrorHandler<IResponseData<INotificationEntityGroup>, IError>(
    {
      queryKey: [plantCrossShippingGroupKey, { queryParameters }],

      queryFn: async () => {
        return await authClient(`${getPlantCrossShippingGroupUrl}`, {
          queryParameters,
          method: 'GET',
        });
      },

      enabled: false,
      refetchOnWindowFocus: false,

      onError: error => {
        firebaseAnalytics.logErrorEvent(
          errors.getNotificationEntitiesGroup,
          error,
          {
            getPlantCrossShippingGroupUrl,
            ...queryParameters,
          },
        );
        openModal(error);
      },

      placeholderData: keepPreviousData,
    },
  );

  const {
    mutateAsync: deletePlantCrossShippingGroup,
    isPending: isPlantCrossShippingGroupDeleting,
    error: deletePlantCrossShippingGroupError,
  } = useMutation<
    IResponseData<boolean>,
    IError,
    { notificationEntityGroupId: number }
  >({
    mutationFn: async payload => {
      return await authClient(`${deletePlantCrossShippingGroupUrl}`, {
        queryParameters: {
          notificationEntityGroupId: payload.notificationEntityGroupId,
        },
        method: 'DELETE',
      });
    },

    retry: false,

    onError: error => {
      firebaseAnalytics.logErrorEvent(
        errors.deleteNotificationEntitiesGroup,
        error,
        {
          deletePlantCrossShippingGroupUrl,
          ...queryParameters,
        },
      );
      openModal(error);
    },
  });

  const {
    mutateAsync: addPlantToCrossShippingGroup,
    isPending: isAddingPlantToCrossShippingGroup,
    isSuccess: isAddingPlantToCrossShippingGroupSuccess,
    error: addingPlantToCrossShippingGroupError,
  } = useMutation({
    mutationFn: (payload: IAddToNotificationEntity) =>
      authClient(addToPlantCrossShippingGroupUrl, {
        method: 'PUT',
        data: payload,
      }),

    retry: false,

    onMutate: async () => {
      await queryClient.cancelQueries({
        queryKey: [plantCrossShippingGroupKey],
      });
      return queryClient.getQueryData<IResponseData<IPlantGroup>>([
        plantCrossShippingGroupKey,
      ]);
    },

    onError: (error: IError, variables, context) => {
      if (context) {
        queryClient.setQueryData<IResponseData<IPlantGroup>>(
          [plantCrossShippingGroupKey],
          context,
        );
      }

      firebaseAnalytics.logErrorEvent(
        errors.addNotificationEntitiesToGroup,
        error,
        {
          addToPlantCrossShippingGroupUrl,
          notificationEntityGroupId: variables.notificationEntityGroupId,
          internalIds: variables.internalIds,
        },
      );
      openModal(error);
    },

    onSettled: () => {
      queryClient.invalidateQueries({
        queryKey: [plantCrossShippingGroupKey],
      });
    },
  });

  const {
    mutateAsync: updatePlantCrossShippingGroup,
    isPending: isUpdatingPlantCrossShippingGroup,
    isSuccess: isUpdatingPlantCrossShippingGroupSuccess,
    error: updatingPlantCrossShippingGroupError,
  } = useMutation({
    mutationFn: (payload: IUpdateNotificationEntityGroup) =>
      authClient(updatePlantCrossShippingGroupUrl, {
        method: 'PUT',
        data: payload,
      }),

    retry: false,

    onMutate: async () => {
      await queryClient.cancelQueries({
        queryKey: [plantCrossShippingGroupKey],
      });
      return queryClient.getQueryData<IResponseData<IPlantGroup>>([
        plantCrossShippingGroupKey,
      ]);
    },

    onError: (error: IError, variables, context) => {
      if (context) {
        queryClient.setQueryData<IResponseData<IPlantGroup>>(
          [plantCrossShippingGroupKey],
          context,
        );
      }

      firebaseAnalytics.logErrorEvent(
        errors.updateNotificationEntitiesGroup,
        error,
        {
          updatePlantCrossShippingGroupUrl,
          notificationEntityGroupId: variables.notificationEntityGroupId,
          notificationEntityGroupName: variables.notificationEntityGroupName,
        },
      );
      openModal(error);
    },

    onSettled: () => {
      queryClient.invalidateQueries({
        queryKey: [plantCrossShippingGroupKey],
      });
    },
  });

  const {
    mutateAsync: removePlantFromCrossShippingGroup,
    isPending: isRemovingPlantFromCrossShippingGroup,
    isSuccess: isRemovingPlantFromCrossShippingGroupSuccess,
    error: removingPlantFromCrossShippingGroupError,
  } = useMutation({
    mutationFn: (payload: IAddToNotificationEntity) =>
      authClient(removePlantFromCrossShippingGroupUrl, {
        method: 'PUT',
        data: payload,
      }),

    retry: false,

    onMutate: async () => {
      await queryClient.cancelQueries({
        queryKey: [plantCrossShippingGroupKey],
      });
      return queryClient.getQueryData<IResponseData<IPlantGroup>>([
        plantCrossShippingGroupKey,
      ]);
    },

    onError: (error: IError, variables, context) => {
      if (context) {
        queryClient.setQueryData<IResponseData<IPlantGroup>>(
          [plantCrossShippingGroupKey],
          context,
        );
      }

      firebaseAnalytics.logErrorEvent(
        errors.removeNotificationEntitiesFromGroup,
        error,
        {
          removePlantFromCrossShippingGroupUrl,
          notificationEntityGroupId: variables.notificationEntityGroupId,
          internalIds: variables.internalIds,
        },
      );
      openModal(error);
    },

    onSettled: () => {
      queryClient.invalidateQueries({
        queryKey: [plantCrossShippingGroupKey],
      });
    },
  });

  const {
    mutateAsync: createPlantCrossShippingGroup,
    isPending: isPlantCrossShippingGroupCreating,
    isSuccess: isPlantCrossShippingGroupSuccess,
    error: createPlantCrossShippingGroupError,
  } = useMutation({
    mutationFn: (payload: ICreateNotificationEntityGroupRequest) =>
      authClient(createPlantCrossShippingGroupUrl, {
        method: 'POST',
        data: payload,
      }),

    retry: false,

    onMutate: async () => {
      await queryClient.cancelQueries({
        queryKey: [plantCrossShippingGroupKey],
      });
      return queryClient.getQueryData<IResponseData<IPlantGroup>>([
        plantCrossShippingGroupKey,
      ]);
    },

    onError: (error: IError, variables, context) => {
      if (context) {
        queryClient.setQueryData<IResponseData<IPlantGroup>>(
          [plantCrossShippingGroupKey],
          context,
        );
      }

      firebaseAnalytics.logErrorEvent(
        errors.createNotificationEntitiesGroup,
        error,
        {
          createPlantCrossShippingGroupUrl,
          notificationEntityGroupName: variables.notificationEntityGroupName,
          internalIds: variables.internalIds,
        },
      );
      openModal(error);
    },
  });

  const plantCrossShippingGroups = useMemo<
    INotificationEntityGroup[] | undefined
  >(
    () => plantCrossShippingGroupsResponse?.data,
    [plantCrossShippingGroupsResponse],
  );

  const plantCrossShippingGroup = useMemo<INotificationEntityGroup | undefined>(
    () => plantCrossShippingGroupResponse?.data,
    [plantCrossShippingGroupResponse?.data],
  );

  return {
    plantCrossShippingGroups,
    fetchPlantCrossShippingGroups,
    isFetchingPlantCrossShippingGroups,
    fetchPlantCrossShippingGroup,
    isFetchingPlantCrossShippingGroup,
    plantCrossShippingGroup,
    deletePlantCrossShippingGroup,
    isPlantCrossShippingGroupDeleting,
    deletePlantCrossShippingGroupError,
    addPlantToCrossShippingGroup,
    isAddingPlantToCrossShippingGroup,
    isAddingPlantToCrossShippingGroupSuccess,
    addingPlantToCrossShippingGroupError,
    updatePlantCrossShippingGroup,
    isUpdatingPlantCrossShippingGroup,
    isUpdatingPlantCrossShippingGroupSuccess,
    updatingPlantCrossShippingGroupError,
    removePlantFromCrossShippingGroup,
    isRemovingPlantFromCrossShippingGroup,
    isRemovingPlantFromCrossShippingGroupSuccess,
    removingPlantFromCrossShippingGroupError,
    createPlantCrossShippingGroup,
    isPlantCrossShippingGroupCreating,
    isPlantCrossShippingGroupSuccess,
    createPlantCrossShippingGroupError,
    crossShippingGroups,
    fetchCrossShippingGroups,
    isFetchingCrossShippingGroups,
    crossShippingGroupsError,
    hasMoreCrossShippingGroups,
  };
}
